import { useCallback, useEffect, useState, type ReactNode } from "react";
import { anchorOf } from "@nabla/api";
import { AskHere, code, Later, useClient, useSession, useTheme, type Point, type Slots } from "../client/index.ts";
import { Banner } from "../design/primitives/Banner.tsx";
import { Button } from "../design/primitives/Button.tsx";
import { Page } from "../design/primitives/Page.tsx";
import { useAskGestures, type Asking } from "./ask-menu/gestures.ts";
import { shellCopy } from "./copy.ts";
import { Banners } from "./errors/Banners.tsx";
import { SignedOut } from "./errors/SignedOut.tsx";
import { Frame } from "./frame/Frame.tsx";
import { useShortcuts } from "./keyboard.ts";
import { NavigationProvider, useNavigation } from "./navigation/Navigation.tsx";
import type { Routes } from "./navigation/router.ts";
import { Toaster } from "./toasts/Toaster.tsx";
import { TopBar } from "./top-bar/TopBar.tsx";
import styles from "./Shell.module.css";
import { designCopy } from "../design/copy.ts";

const parts = code(() => import("./parts.ts"));
type Parts = typeof import("./parts.ts");

function Opened({ open, children }: { readonly open: boolean; readonly children: (shell: Parts) => ReactNode }) {
  const [opened, setOpened] = useState(open);
  if (open && !opened) setOpened(true);
  return opened || open ? <Later parts={parts}>{children}</Later> : null;
}

export function Shell({ routes, slots }: { readonly routes: Routes; readonly slots: Slots }) {
  useDocumentTheme();
  return (
    <NavigationProvider routes={routes}>
      <Layout slots={slots} />
    </NavigationProvider>
  );
}

function Layout({ slots }: { readonly slots: Slots }) {
  const session = useSession();
  const client = useClient();
  const { route } = useNavigation();
  if (route.match?.entry.public) {
    return (
      <main id="conteudo" tabIndex={-1}>
        <Frame />
      </main>
    );
  }
  if (session.status === "fora") return <SignedOut reason={session.reason} />;
  if (session.status === "carregando") return <main id="conteudo" className={styles.waiting} aria-busy="true" />;
  if (session.status === "erro") {
    return (
      <main id="conteudo">
        <Page>
          <Banner tone="negativo" action={<Button onClick={() => client.start()}>{designCopy.tentarDeNovo}</Button>}>
            {shellCopy.tela.naoCarregou}
          </Banner>
        </Page>
      </main>
    );
  }
  return <Inside slots={slots} />;
}

function Inside({ slots }: { readonly slots: Slots }) {
  const navigation = useNavigation();
  const session = useSession();
  const [drawer, setDrawer] = useState(false);
  const [help, setHelp] = useState(false);
  const [signInLink, setSignInLink] = useState(false);
  const [searchKey, setSearchKey] = useState(0);
  const [asking, setAsking] = useState<Asking | null>(null);
  const closeDrawer = useCallback(() => setDrawer(false), []);
  const closeAsk = useCallback(() => setAsking(null), []);
  const asks = session.status === "dentro" && session.view.derived.modules.conversas;
  const asksHere = asks && navigation.route.match?.entry.level !== "conversa";
  useAskGestures(asks ? setAsking : undefined);
  useShortcuts({ back: navigation.back, zoomOut: navigation.zoomOut, sibling: navigation.sibling, search: () => setSearchKey((key) => key + 1), help: () => setHelp(true) });

  const askHere = (at: Point) => {
    const self = navigation.facts()?.self;
    const anchor = self ? anchorOf(self) : undefined;
    if (self && anchor) return setAsking({ anchor, name: self.name, at });
    if (session.status !== "dentro") return;
    setAsking({ anchor: { place: "empresa", id: session.view.workspace.id }, name: session.view.ui.brand.name, at });
  };

  return (
    <AskHere.Provider value={asks ? askHere : undefined}>
      <a className={styles.skip} href="#conteudo">
        {shellCopy.pularParaConteudo}
      </a>
      <TopBar onMenu={() => setDrawer(true)} onHelp={() => setHelp(true)} onAsk={asksHere ? askHere : undefined}
        searchKey={searchKey} Answers={slots.answers} />
      <Banners />
      <div className={styles.body}>
        {asks && slots.sidebar ? <aside className={styles.sidebar} aria-label={shellCopy.conversas}><slots.sidebar /></aside> : null}
        <main id="conteudo" tabIndex={-1} className={styles.main}>
          <Frame />
        </main>
      </div>
      <Opened open={drawer}>
        {({ Drawer }) => <Drawer open={drawer} onClose={closeDrawer} onSignInLink={() => setSignInLink(true)} />}
      </Opened>
      <Opened open={help}>
        {({ Shortcuts }) => <Shortcuts open={help} onClose={() => setHelp(false)} />}
      </Opened>
      <Opened open={signInLink}>
        {({ SignInLink }) => <SignInLink open={signInLink} onClose={() => setSignInLink(false)} />}
      </Opened>
      {asking ? <Later parts={parts}>{({ AskMenu }) => <AskMenu asking={asking} onClose={closeAsk} Slot={slots.ask} />}</Later> : null}
      <Toaster />
    </AskHere.Provider>
  );
}

function useDocumentTheme(): void {
  const [theme] = useTheme();
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "sistema") delete root.dataset["theme"];
    else root.dataset["theme"] = theme === "claro" ? "light" : "dark";
  }, [theme]);
}
